<template>
  <div
    class="relative flex gap-2 b rounded-xs bg-grey-90 px-2 py-4"
    :class="modelValue === type ? 'b-grey-10' : 'b-transparent'"
  >
    <vf-icon  :name="icon" size="md" />
    <span class="w-full f-col gap-1 text-sm">
      <base-radio
        v-if="selectable"
        v-model="modelValue"
        :aria-describedby="[type === 'pickup' ? 'pickup-store' : '', `${type}-description`].join(' ')"
        class="|static"
        :name="type"
        :value="type"
        @change="$emit('change', $event)"
      >
        <span class="fw-medium peer-focus-visible:outline-auto">
          {{ label }}
        </span>
        <span v-if="modelValue !== type" class="absolute-0 cursor-pointer" />
      </base-radio>
      <span v-else class="fw-medium ">
        {{ label }}
      </span>
      <span v-if="type === 'standard'" id="standard-description" >
        <span class="f-col gap-1">
          <span
            v-if="showEdd"
            class="f-col gap-1"
            data-narvar-feature="promiseEddCalculator"
            style="min-height: 3.2rem"
            @click="$gtm.push('pdpPage.onEstimatedDeliveryDate')"
          >
            <div class="f-col gap-2">
              <div class="h-4 w-1/4 skeleton" />
              <div class="h-5 w-1/3 skeleton" />
            </div>
          </span>
          <template v-else>
            <span>
              {{ $t.standardShipping }}
            </span>
            <span v-if="description">
              {{ description }}
            </span>
          </template>
        </span>
      </span>
      <template v-else-if="type === 'pickup'">
        <span v-if="selectedStore" id="pickup-store" >
          {{ selectedStore.name }}
        </span>
        <base-button class="relative z-1 self-start py-px underlined" @click="$emit('open-search')">
          {{ selectedStore ? $t.changeStore.title : $t.findStore }}
        </base-button>
        <span v-if="description" id="pickup-description"  :class="{ 'c-red-30': error }">
          {{ description }}.
        </span>
      </template>
    </span>
  </div>
</template>

<script lang="ts" setup>
import type { LocatorSearchItem } from '#types/brandify'
import type { IconName } from '#types/icon'
import type { SelectedVariant } from '#types/product'

declare global {
  interface Window {
    narvar: (targetElement: string, config: object) => void
  }
}

const { selectable = true, variant, showEdd } = defineProps<{
  description?: string
  error?: boolean
  icon?: IconName
  label: string
  selectable?: boolean
  selectedStore?: LocatorSearchItem
  showEdd?: boolean
  type: 'standard' | 'pickup'
  variant?: SelectedVariant
}>()

defineEmits<{
  'change': [value: string]
  'open-search': []
}>()

const modelValue = defineModel<string>()

const auth = useAuthStore()
const xUsidCookie = useCookie('x-usid')
const localeCountry = useCountryCode()
const localeLanguage = useLanguageCode().toLowerCase()
const { $feature } = useNuxtApp()
const { load, remove } = useScript({
  src: $feature.configNarvarUrl,
  defer: true,
  crossorigin: false
}, { mode: 'client', trigger: 'manual' })
const user = useUserStore()

const loadCss = async () => new Promise((resolve, reject) => {
  try {
    useHead({
      link: () => [
        {
          rel: 'stylesheet',
          type: 'text/css',
          href: '/narvar/css/NarvarEstimatedDelivery.css',
          once: true,
          onload: () => resolve(true),
          // sometimes it throws errors even when loading, so we just have to wait for the event to kick in so we don't block the loading flow, thats why its resolving and not rejecting
          onerror: () => resolve(false),
        }
      ],
    })
  }
  catch (err) {
    reject(err)
    console.warn('failed to load narvar estimated delivery css', err)
  }
})

const estimateDeliveryDate = async (variant?: SelectedVariant) => {
  await loadCss()
  await load()

  window.narvar('promiseEddCalculator', {
    customerId: auth.consumerNo || xUsidCookie.value, // Authenticated user id or x-usid when unauthenticated
    localeLanguage,
    localeCountry,
    sessionId: xUsidCookie.value,
    platform: 'sfcc',
    store: location.origin,
    destZip: user.defaultShippingAddress?.postalCode,
    destCountry: user.defaultShippingAddress?.countryCode.toUpperCase(),
    itemSku: variant?.id,
    itemQuantity: 1,
    itemOriginalPriceAmount: variant?.price.current,
    itemOriginalPriceCurrency: useCurrencyCode(),
    itemInStock: variant?.buyable
  })
}

watchImmediate(() => variant, () => {
  if (showEdd) estimateDeliveryDate(variant)
})

onBeforeUnmount(() => remove())
</script>
